import React, {ReactNode, useState} from "react"
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	FormControlLabel,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material"

import {
	SMS_MAX_CHARACTERS,
	SMS_REPLY_FACTS,
	SMS_SEGMENT_LIMIT,
	characterCount,
	replyBudget,
	segmentCount,
} from "./smsReplyBudget"

import {colors} from "@indebted/theme/colors"

type SMSDraft = {
	text: string
	modelVersion: string
}

type GenerateDraftRequest = {
	guidance: string
	fields: string[]
	replyInCustomerLanguage: boolean
}

type BlockedReason = "prompt_injection" | "conduct_exhausted"

type GenerateDraftResult =
	| {status: "ok"; draft: SMSDraft; omittedFields?: string[]; contactWarning?: string}
	| {status: "blocked"; reason: BlockedReason}

type SendReplyRequest = {
	text: string
	guidance: string
	modelVersion: string
}

type SMSDraftReplyPanelProps = {
	fromNumber: string
	toNumber: string
	authoredBy?: string
	onGenerateDraft?: (request: GenerateDraftRequest) => Promise<GenerateDraftResult>
	onSendReply?: (reply: SendReplyRequest) => Promise<void>
}

type SendState = {kind: "idle"} | {kind: "sent"} | {kind: "error"}

// Name is on by default so a draft greets the customer without an extra click per message.
const DEFAULT_FIELDS = ["customer_name"]

function SMSDraftReplyPanel({fromNumber, toNumber, authoredBy, onGenerateDraft, onSendReply}: SMSDraftReplyPanelProps) {
	const [guidance, setGuidance] = useState("")
	const [fields, setFields] = useState<string[]>(DEFAULT_FIELDS)
	const [replyInCustomerLanguage, setReplyInCustomerLanguage] = useState(true)
	const [draft, setDraft] = useState<SMSDraft | null>(null)
	const [omittedFields, setOmittedFields] = useState<string[]>([])
	const [contactWarning, setContactWarning] = useState("")
	const [blockedReason, setBlockedReason] = useState<BlockedReason | null>(null)
	const [text, setText] = useState("")
	const [generating, setGenerating] = useState(false)
	const [sending, setSending] = useState(false)
	const [generateFailed, setGenerateFailed] = useState(false)
	const [sendState, setSendState] = useState<SendState>({kind: "idle"})

	const budget = replyBudget(fields)

	function toggleField(field: string, checked: boolean) {
		setFields((current) => (checked ? [...current, field] : current.filter((f) => f !== field)))
	}

	async function generateDraft() {
		if (!onGenerateDraft) return
		setGenerating(true)
		setGenerateFailed(false)
		setBlockedReason(null)
		setSendState({kind: "idle"})
		try {
			const result = await onGenerateDraft({guidance, fields, replyInCustomerLanguage})
			if (result.status === "blocked") {
				// A block leaves no draft to send, so the previous one goes with it
				// rather than sitting there looking approvable.
				setDraft(null)
				setBlockedReason(result.reason)
				return
			}
			setDraft(result.draft)
			setText(result.draft.text)
			setOmittedFields(result.omittedFields ?? [])
			setContactWarning(result.contactWarning ?? "")
		} catch {
			setGenerateFailed(true)
		} finally {
			setGenerating(false)
		}
	}

	async function sendReply() {
		if (!onSendReply || !draft) return
		setSending(true)
		try {
			await onSendReply({text, guidance, modelVersion: draft.modelVersion})
			setSendState({kind: "sent"})
		} catch {
			setSendState({kind: "error"})
		} finally {
			setSending(false)
		}
	}

	return (
		<PanelColumn>
			<Box display="flex" alignItems="center" gap={1}>
				<Typography variant="subtitle2" color="textPrimary">
					Draft a reply
				</Typography>
				<Chip label="SMS" size="small" />
			</Box>

			<Box borderRadius={1} p={1.5} sx={{bgcolor: colors.gray["100"]}}>
				<NumberRow label="From" number={fromNumber} />
				<NumberRow label="To" number={toNumber} />
				<Typography variant="caption" color="textSecondary">
					Taken from the message you are replying to. Not editable.
				</Typography>
			</Box>

			<TextField
				label="Instructions"
				placeholder="e.g. offer a 30-day hardship pause"
				value={guidance}
				onChange={(event) => setGuidance(event.target.value)}
				multiline
				minRows={4}
				fullWidth
				size="small"
			/>

			<FieldSet label={`Include these customer details · budget ${SMS_SEGMENT_LIMIT} characters`}>
				<Box display="grid" gridTemplateColumns="repeat(2, 1fr)" columnGap={2}>
					{SMS_REPLY_FACTS.map(({field, label, estimatedCharacters}) => (
						<FormControlLabel
							key={field}
							control={
								<Checkbox
									checked={fields.includes(field)}
									onChange={(event) => toggleField(field, event.target.checked)}
								/>
							}
							label={
								<Box display="flex" alignItems="baseline" gap={0.75}>
									<Typography variant="body2">{label}</Typography>
									<Typography variant="caption" color="textSecondary">
										~{estimatedCharacters}
									</Typography>
								</Box>
							}
						/>
					))}
				</Box>
				<LinearProgress
					variant="determinate"
					value={Math.min((budget.reserved / budget.limit) * 100, 100)}
					color={budget.overBudget ? "error" : "primary"}
				/>
				<Typography variant="caption" color={budget.overBudget ? "error" : "textSecondary"}>
					{budget.reserved} of {budget.limit} characters reserved for facts · {budget.remaining} left for the
					message
				</Typography>
			</FieldSet>

			<FieldSet label="Reply language">
				<FormControlLabel
					control={
						<Checkbox
							checked={replyInCustomerLanguage}
							onChange={(event) => setReplyInCustomerLanguage(event.target.checked)}
						/>
					}
					label={<Typography variant="body2">Reply in the customer&apos;s language</Typography>}
				/>
			</FieldSet>

			<Button
				variant="contained"
				color="primary"
				disabled={!onGenerateDraft || generating}
				onClick={generateDraft}
				sx={{alignSelf: "flex-start"}}
			>
				{generating ? <CircularProgress size={24} /> : "Generate draft"}
			</Button>
			{!onGenerateDraft && (
				<Typography variant="caption" color="textSecondary">
					Drafting an SMS reply is not available yet.
				</Typography>
			)}
			{generateFailed && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't generate the draft. Try again."}
				</Alert>
			)}
			{blockedReason && <BlockedBanner reason={blockedReason} />}

			{draft && (
				<>
					<Divider />
					{sendState.kind === "sent" ? (
						<Alert severity="success" sx={{borderRadius: 2}}>
							Reply sent
						</Alert>
					) : (
						<DraftPreview
							text={text}
							omittedFields={omittedFields}
							contactWarning={contactWarning}
							onTextChange={setText}
							onSend={sendReply}
							canSend={Boolean(onSendReply)}
							signedIn={Boolean(authoredBy)}
							sending={sending}
							sendFailed={sendState.kind === "error"}
						/>
					)}
				</>
			)}
		</PanelColumn>
	)
}

function DraftPreview({
	text,
	omittedFields,
	contactWarning,
	onTextChange,
	onSend,
	canSend,
	signedIn,
	sending,
	sendFailed,
}: {
	text: string
	omittedFields: string[]
	contactWarning: string
	onTextChange: (text: string) => void
	onSend: () => void
	canSend: boolean
	signedIn: boolean
	sending: boolean
	sendFailed: boolean
}) {
	const segments = segmentCount(text)
	const overOneSegment = segments > 1
	const characters = characterCount(text)
	const overLength = characters > SMS_MAX_CHARACTERS
	const omittedLabels = omittedFields
		.map((field) => SMS_REPLY_FACTS.find((fact) => fact.field === field)?.label)
		.filter((label): label is string => Boolean(label))

	return (
		<PanelColumn>
			<Typography variant="subtitle2" color="textPrimary">
				Drafted message
			</Typography>
			{contactWarning !== "" && (
				<Alert severity="warning" sx={{borderRadius: 2}}>
					{`Active ${contactWarning} case on this customer.`}
				</Alert>
			)}
			<TextField
				value={text}
				onChange={(event) => onTextChange(event.target.value)}
				multiline
				fullWidth
				size="small"
			/>
			<Typography
				variant="caption"
				color={overLength ? "error" : overOneSegment ? "warning.main" : "textSecondary"}
			>
				{characters} characters · {segments} segment{segments === 1 ? "" : "s"}
			</Typography>
			{overLength && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{`Too long to send — trim to ${SMS_MAX_CHARACTERS} characters.`}
				</Alert>
			)}
			{omittedLabels.length > 0 && (
				<Alert severity="info" sx={{borderRadius: 2}}>
					Not included — no data on file: {omittedLabels.join(", ")}
				</Alert>
			)}
			{sendFailed && (
				<Alert severity="error" sx={{borderRadius: 2}}>
					{"Couldn't send the reply. Try again."}
				</Alert>
			)}
			<Button
				variant="contained"
				color="primary"
				disabled={!canSend || !signedIn || sending || overLength || text.trim() === ""}
				onClick={onSend}
				sx={{alignSelf: "flex-start"}}
			>
				{sending ? <CircularProgress size={24} /> : "Approve & send"}
			</Button>
			{!canSend ? (
				<Typography variant="caption" color="textSecondary">
					Sending an SMS reply is not available yet.
				</Typography>
			) : (
				!signedIn && (
					<Typography variant="caption" color="textSecondary">
						Sign in to send replies.
					</Typography>
				)
			)}
		</PanelColumn>
	)
}

function BlockedBanner({reason}: {reason: BlockedReason}) {
	const message =
		reason === "prompt_injection"
			? "This reply was blocked for a suspected prompt injection in the customer message."
			: "Compliance review failed every attempt, so no draft passed. Adjust the instructions and try again."

	return (
		<Alert severity="warning" sx={{borderRadius: 2}}>
			{message}
		</Alert>
	)
}

function NumberRow({label, number}: {label: string; number: string}) {
	return (
		<Box display="flex" gap={2}>
			<Typography variant="caption" color="textSecondary" width={40}>
				{label}
			</Typography>
			<Typography variant="body2" color="textPrimary" fontWeight={600}>
				{number}
			</Typography>
		</Box>
	)
}

function PanelColumn({children}: {children: ReactNode}) {
	return (
		<Box display="flex" flexDirection="column" gap={2}>
			{children}
		</Box>
	)
}

function FieldSet({label, children}: {label: string; children: ReactNode}) {
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			border="1px solid"
			borderColor="grey.200"
			borderRadius={1}
			p={1.5}
		>
			<Typography variant="caption" color="textSecondary" textTransform="uppercase" letterSpacing={0.5}>
				{label}
			</Typography>
			{children}
		</Box>
	)
}

export {SMSDraftReplyPanel}
export type {SMSDraft, GenerateDraftRequest}
